/*
 * Winterborn storefront behaviour.
 * Small, dependency-free helpers layered on Horizon.
 */
(function () {
  'use strict';

  /* ---------- Delivery estimate ---------- */
  function addBusinessDays(date, days) {
    var d = new Date(date);
    while (days > 0) {
      d.setDate(d.getDate() + 1);
      if (d.getDay() !== 0 && d.getDay() !== 6) days--;
    }
    return d;
  }

  function initDeliveryEstimates(root) {
    var nodes = (root || document).querySelectorAll('[data-wb-delivery]:not([data-wb-ready])');
    if (!nodes.length) return;

    var fmt = { month: 'short', day: 'numeric' };
    var locale = document.documentElement.lang || undefined;
    var now = new Date();

    nodes.forEach(function (el) {
      var start = addBusinessDays(now, parseInt(el.dataset.min, 10) || 0).toLocaleDateString(locale, fmt);
      var end = addBusinessDays(now, parseInt(el.dataset.max, 10) || 0).toLocaleDateString(locale, fmt);
      el.textContent = (el.dataset.template || '').replace('[start]', start).replace('[end]', end);
      el.hidden = false;
      el.setAttribute('data-wb-ready', '');
    });
  }

  /* ---------- Simple scroll carousel ----------
   * <div data-wb-carousel>
   *   <button data-wb-prev> <button data-wb-next>
   *   <div data-wb-track> ...children... </div>
   * </div> */
  function initCarousels(root) {
    (root || document).querySelectorAll('[data-wb-carousel]:not([data-wb-ready])').forEach(function (el) {
      var track = el.querySelector('[data-wb-track]');
      if (!track) return;
      el.setAttribute('data-wb-ready', '');

      function step() {
        var first = track.children[0];
        if (!first) return track.clientWidth;
        var gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        return first.getBoundingClientRect().width + gap;
      }

      function update() {
        var prev = el.querySelector('[data-wb-prev]');
        var next = el.querySelector('[data-wb-next]');
        var max = track.scrollWidth - track.clientWidth - 2;
        if (prev) prev.disabled = track.scrollLeft <= 2;
        if (next) next.disabled = track.scrollLeft >= max;
      }

      el.querySelectorAll('[data-wb-prev], [data-wb-next]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var dir = btn.hasAttribute('data-wb-prev') ? -1 : 1;
          track.scrollBy({ left: dir * step(), behavior: 'smooth' });
        });
      });

      track.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      update();
    });
  }

  /* ---------- Stacked testimonial cards ---------- */
  function initStacks(root) {
    (root || document).querySelectorAll('[data-wb-stack]:not([data-wb-ready])').forEach(function (el) {
      var cards = Array.prototype.slice.call(el.querySelectorAll('[data-wb-stack-card]'));
      if (!cards.length) return;
      el.setAttribute('data-wb-ready', '');
      var index = 0;
      var timer = null;
      var delay = parseInt(el.dataset.autoplay, 10) || 0;

      function render() {
        cards.forEach(function (card, i) {
          var pos = (i - index + cards.length) % cards.length;
          card.setAttribute('data-pos', pos < 3 ? pos : 'hidden');
          card.setAttribute('aria-hidden', pos === 0 ? 'false' : 'true');
        });
      }

      function go(dir) {
        index = (index + dir + cards.length) % cards.length;
        render();
      }

      function restart() {
        if (!delay) return;
        clearInterval(timer);
        timer = setInterval(function () { go(1); }, delay * 1000);
      }

      var prev = el.querySelector('[data-wb-prev]');
      var next = el.querySelector('[data-wb-next]');
      if (prev) prev.addEventListener('click', function () { go(-1); restart(); });
      if (next) next.addEventListener('click', function () { go(1); restart(); });
      el.addEventListener('mouseenter', function () { clearInterval(timer); });
      el.addEventListener('mouseleave', restart);

      render();
      restart();
    });
  }

  /* ---------- Scroll reveal ---------- */
  function initReveal() {
    var items = document.querySelectorAll('.wb-reveal:not(.is-visible)');
    if (!items.length) return;
    if (!('IntersectionObserver' in window)) {
      items.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -10% 0px' }
    );
    items.forEach(function (el) { io.observe(el); });
  }

  /* ---------- Top-level menu items with a dropdown: open it on click instead of navigating ----------
   Desktop header: clicking "Products" opens the dropdown (same as hovering) and never follows the
   link. The link target stays available as the "All products" button inside the dropdown. */
  function initMenuClickOpen() {
    if (document.documentElement.hasAttribute('data-wb-menu-click')) return;
    document.documentElement.setAttribute('data-wb-menu-click', '');
    document.addEventListener('click', function (event) {
      var target = event.target;
      if (!(target instanceof Element)) return;
      var link = target.closest('.menu-list__trigger > a.menu-list__link');
      if (!link) return;
      event.preventDefault();
      var disclosure = link.parentElement && link.parentElement.querySelector('.menu-list__disclosure');
      // Hover already opens it; only toggle it open if it is closed (touch, keyboard)
      if (disclosure && disclosure.getAttribute('aria-expanded') !== 'true') disclosure.click();
    });
  }

  /* ---------- Grouped Category filter (parent categories with nested types) ----------
   - Ticking a parent ticks or clears all of its children, then runs Horizon's normal filter update once.
   - The arrow opens and closes a group. */
  // Redraw every group's parent box from its children straight away, without waiting for the server:
  // all nested boxes ticked = ticked, some = a dash, none = empty
  function syncCategoryStates() {
    document.querySelectorAll('.wb-cat__group').forEach(function (group) {
      var kids = group.querySelectorAll('.wb-cat__children input[type="checkbox"]:not(:disabled)');
      var parent = group.querySelector('.wb-cat__parent-input');
      if (!kids.length || !parent) return;
      var on = 0;
      kids.forEach(function (k) { if (k.checked) on++; });
      var state = on === 0 ? 'none' : on === kids.length ? 'checked' : 'partial';
      group.setAttribute('data-state', state);
      if (group.classList.contains('wb-cat__group--nested')) {
        // a nested parent is a real type: partial still counts as ticked (it carries the type), none does not
        if (state === 'none' && !parent.hasAttribute('data-wb-keep')) parent.checked = false;
        if (state !== 'none') parent.checked = true;
      } else {
        parent.checked = state === 'checked';
      }
    });
  }

  // Apply the form's filters ourselves: build the address from the form (Horizon's own method) and hand it to
  // Horizon's "apply this address" method. Returns false when Horizon's methods are not available.
  function applyFiltersFromForm(source) {
    var form = source.closest('form');
    var facets = source.closest('facets-form-component');
    if (!form || !facets || typeof facets.createURLParameters !== 'function' || typeof facets.updateFiltersByURL !== 'function') {
      return false;
    }
    var params = facets.createURLParameters(new FormData(form));
    var url = new URL(window.location.href);
    url.search = '';
    params.forEach(function (value, key) { url.searchParams.append(key, value); });
    facets.updateFiltersByURL(url.toString());
    return true;
  }

  // Open a category group straight away (the server's re-draw then agrees, because a group with a ticked child renders open)
  function openCategoryGroup(group) {
    if (!group) return;
    var toggle = group.querySelector('[data-wb-cat-toggle]');
    var list = group.querySelector('.wb-cat__children');
    if (toggle) toggle.setAttribute('aria-expanded', 'true');
    if (list) list.hidden = false;
  }

  function initCategoryGroups() {
    if (document.documentElement.hasAttribute('data-wb-cat')) return;
    document.documentElement.setAttribute('data-wb-cat', '');

    // Capture phase: run before Horizon's own change handler, which would otherwise see the parent box
    document.addEventListener('change', function (event) {
      var target = event.target;
      if (target instanceof Element && target.closest('.wb-cat__group')) setTimeout(syncCategoryStates, 0);

      // Ticking a parent that is a single real type (Scarves, Socks, Mittens & Gloves) shows all of its nested
      // boxes ticked (or clears them), but sends ONLY the type: the type already means "all of them". The nested
      // boxes lose their name so Horizon leaves them out of the request; the server then draws them ticked again.
      if (target instanceof Element && target.matches('input[data-wb-cat-nested-parent]')) {
        var nestedGroup = target.closest('.wb-cat__group');
        if (nestedGroup) {
          nestedGroup.querySelectorAll('.wb-cat__children input[type="checkbox"]:not(:disabled)').forEach(function (box) {
            box.checked = target.checked;
            box.name = '';
          });
        }
        if (target.checked) openCategoryGroup(nestedGroup);
        target.removeAttribute('data-wb-auto');
        syncCategoryStates();
        if (applyFiltersFromForm(target)) event.stopImmediatePropagation();
        return;
      }

      // A nested type (for example Plaids under Scarves) also needs its parent type ticked, so the
      // results stay inside that category (Pattern 'Solid' also exists on non-scarves).
      var needs = target instanceof Element && target.matches('input[type="checkbox"]') ? target.closest('[data-wb-cat-needs]') : null;
      if (needs) {
        // The user is choosing among the nested boxes, so from here each one counts on its own:
        // give every sibling its real name back before the request is built
        var siblingGroup = needs.closest('.wb-cat__group');
        if (siblingGroup) {
          siblingGroup.querySelectorAll('.wb-cat__children [data-wb-cat-name]').forEach(function (wrapper) {
            var box = wrapper.querySelector('input[type="checkbox"]');
            if (box && !box.name) box.name = wrapper.getAttribute('data-wb-cat-name');
          });
        }
        var parentInput = document.getElementById(needs.getAttribute('data-wb-cat-needs'));
        var children = needs.closest('.wb-cat__children');
        // Another category is ticked: filters of different kinds narrow each other, so "Headwear + Sport Socks only"
        // cannot be asked of Shopify (it would return nothing). The sub-option you pick stays exactly that one,
        // and the other ticked categories are cleared.
        if (target.checked && parentInput) {
          document.querySelectorAll('.wb-cat input[type="checkbox"]:checked').forEach(function (box) {
            if (!siblingGroup.contains(box)) box.checked = false;
          });
        }
        if (target.checked) openCategoryGroup(needs.closest('.wb-cat__group'));
        if (parentInput && target.checked && !parentInput.checked) {
          parentInput.checked = true;
          parentInput.setAttribute('data-wb-auto', '');
        } else if (parentInput && !target.checked && children && !children.querySelector('input:checked')) {
          // Nothing left chosen under it: clear the parent too (otherwise the bare type would mean "all" again)
          parentInput.checked = false;
          parentInput.removeAttribute('data-wb-auto');
        }
        syncCategoryStates();
        if (applyFiltersFromForm(target)) event.stopImmediatePropagation(); // else Horizon's own handler runs
        return;
      }

      if (!(target instanceof Element) || !target.matches('[data-wb-cat-parent]')) return;
      event.stopImmediatePropagation();
      var group = target.closest('.wb-cat__group');
      if (!group) return;
      var inputs = group.querySelectorAll('.wb-cat__children input[type="checkbox"]:not(:disabled)');
      if (!inputs.length) return;
      inputs.forEach(function (input) { input.checked = target.checked; });
      if (target.checked) openCategoryGroup(group);
      inputs[0].dispatchEvent(new Event('change', { bubbles: true }));
    }, true);

    // Pills drawn for nested categories: removing one unticks its box in the sidebar
    function removeCategoryPill(pill) {
      var name = pill.getAttribute('data-wb-cat-pill-name');
      var value = pill.getAttribute('data-wb-cat-pill-value');
      var boxes = document.querySelectorAll('.facets__form input[type="checkbox"]');
      for (var i = 0; i < boxes.length; i++) {
        var box = boxes[i];
        if (box.value !== value) continue;
        var wrapper = box.closest('[data-wb-cat-name]');
        var boxName = box.name || (wrapper ? wrapper.getAttribute('data-wb-cat-name') : '');
        if (boxName === name) {
          box.click();
          return;
        }
      }
    }

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      var target = event.target;
      if (!(target instanceof Element)) return;
      var pill = target.closest('[data-wb-cat-pill]');
      if (!pill) return;
      event.preventDefault();
      removeCategoryPill(pill);
    });

    document.addEventListener('click', function (event) {
      var target = event.target;
      if (!(target instanceof Element)) return;
      var pill = target.closest('[data-wb-cat-pill]');
      if (pill) {
        removeCategoryPill(pill);
        return;
      }
      var button = target.closest('[data-wb-cat-toggle]');
      if (!button) return;
      var group = button.closest('.wb-cat__group');
      var list = group && group.querySelector('.wb-cat__children');
      if (!list) return;
      var open = button.getAttribute('aria-expanded') === 'true';
      button.setAttribute('aria-expanded', String(!open));
      list.hidden = open;
    });
  }

  function init() {
    initDeliveryEstimates();
    initCarousels();
    initStacks();
    initReveal();
    initMenuClickOpen();
    initCategoryGroups();
  }

  /* ---------- Vitals pack dropdown: the open list is as wide as the button that opened it ---------- */
  (function matchVitalsDropdownWidth() {
    function fit() {
      var open = document.querySelector('.Vtl-Dropdown__Header-ArrowContainer--IsOpen');
      var header = open && open.closest('.Vtl-Dropdown__Header');
      if (!header) return;
      var width = Math.round(header.getBoundingClientRect().width);
      document.querySelectorAll('.Vtl-DropdownDialog, .Vtl-Dropdown__Portal-List').forEach(function (el) {
        el.style.setProperty('width', width + 'px', 'important');
        el.style.setProperty('min-width', width + 'px', 'important');
        el.style.setProperty('max-width', 'none', 'important');
      });
    }
    // Flags the CSS reads (cheaper than body:has(...), which the browser re-checks on every change anywhere on the page)
    function syncPackFlags() {
      var body = document.body;
      body.toggleAttribute('data-wb-packs', !!document.querySelector('.Vtl-NewVolumeDiscountRadio'));
      body.toggleAttribute('data-wb-pack-selected', !!document.querySelector('.Vtl-NewVolumeDiscountRadio__Radio--Checked'));
    }
    var queued = false;
    function schedule() {
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () {
        queued = false;
        fit();
        syncPackFlags();
      });
    }
    new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true });
    // Vitals changes the checked pack with a class change, not a DOM insert
    document.addEventListener('click', function () { setTimeout(syncPackFlags, 50); }, true);
    document.addEventListener('change', function () { setTimeout(syncPackFlags, 50); }, true);
    syncPackFlags();
    // Vitals places the list once when it opens, so close it on resize instead of leaving it misplaced
    window.addEventListener('resize', function () {
      var open = document.querySelector('.Vtl-Dropdown__Header-ArrowContainer--IsOpen');
      var header = open && open.closest('.Vtl-Dropdown__Header');
      if (header) header.click();
    });
  })();


  // Keep scroll position when the scroller switches at 990px (fixes blank screen)
  (function () {
    var wide = window.matchMedia('(min-width: 990px)');
    var doc = document.documentElement;
    var wrap = document.querySelector('.page-wrapper') || doc;
    var y = 0;
    document.addEventListener('scroll', function () {
      y = (wide.matches ? wrap : doc).scrollTop;
    }, { capture: true, passive: true });
    wide.addEventListener('change', function () {
      doc.scrollTop = wrap.scrollTop = 0;
      (wide.matches ? wrap : doc).scrollTop = y;
    });
  })();

  // Vitals placeholders sit in fixed-height slots (heights in assets/wb-theme.css). If the widget never comes
  // (no offer on this product), drop the shimmer after 8s so no empty box is left behind.
  function dropSkeletonLater(slot, selector) {
    if (slot.dataset.wbSkelTimer) return;
    slot.dataset.wbSkelTimer = 'true';
    setTimeout(function () {
      var skel = slot.querySelector(selector);
      if (skel) skel.remove();
    }, 8000);
  }

  // Vitals' own outermost wrapper around an element (its class or id names Vitals), never one of the theme's blocks
  function vitalsRoot(el, slot) {
    var vitals = /vtl|vitals/i;
    while (
      el.parentElement &&
      !el.parentElement.contains(slot) &&
      vitals.test(el.parentElement.className + ' ' + el.parentElement.id)
    ) {
      el = el.parentElement;
    }
    return el;
  }

  // Product page: the Shop Pay installments line keeps its shimmer (assets/wb-theme.css) until Shopify has drawn
  // it. It is drawn inside a shadow root the page cannot watch, so check its height every 100ms; give up after
  // 10s (the box still keeps its height, only the shimmer goes).
  (function () {
    var pending = '.product-information .installments:not(.is-drawn)';
    var timer = null;
    var started = 0;
    function tick() {
      var timedOut = Date.now() - started > 10000;
      document.querySelectorAll(pending).forEach(function (box) {
        var terms = box.querySelector('shopify-payment-terms');
        if (timedOut || (terms && terms.getBoundingClientRect().height > 0)) box.classList.add('is-drawn');
      });
      if (!document.querySelector(pending)) {
        clearInterval(timer);
        timer = null;
      }
    }
    function start() {
      if (timer || !document.querySelector(pending)) return;
      started = Date.now();
      timer = setInterval(tick, 100);
      tick();
    }
    // A size / colour change re-renders the price block with a fresh, undrawn line
    new MutationObserver(start).observe(document.body, { childList: true, subtree: true });
    start();
  })();

  // Product page: move the Vitals "Only N units left" line into its fixed slot right above the packs
  (function () {
    if (!document.querySelector('[data-wb-stock-slot]')) return;
    function place() {
      var slot = document.querySelector('[data-wb-stock-slot]:not(.quick-add-modal *)');
      if (slot) dropSkeletonLater(slot, '.wb-skel-stock');
      var line = document.querySelector('.vtl-su-main-widget:not(.quick-add-modal *)');
      if (!slot || !line) return;
      if (!slot.contains(line)) slot.appendChild(vitalsRoot(line, slot));
      // Vitals adds the box first and its text a moment later: keep the shimmer (and the box hidden, see
      // assets/wb-theme.css) until the message is there, or the slot collapses for a frame and jumps
      var message = line.querySelector('.vtl-su-main-widget__message');
      var skel = slot.querySelector('.wb-skel-stock');
      if (skel && message && message.textContent.trim()) skel.remove();
    }
    var queued = false;
    // Kept on: a size / colour change re-renders the buy buttons and Vitals adds the line again
    new MutationObserver(function () {
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () {
        queued = false;
        place();
      });
    }).observe(document.body, { childList: true, subtree: true, characterData: true });
    place();
  })();

  // Product page: move the Vitals pack options into their fixed slot above Add to cart, wherever Vitals
  // inserted them. The whole node Vitals added is moved, so its own rendering keeps working.
  (function () {
    if (!document.querySelector('[data-wb-packs-slot]')) return;
    function place() {
      var slot = document.querySelector('[data-wb-packs-slot]:not(.quick-add-modal *)');
      if (slot) dropSkeletonLater(slot, '.wb-skel-packs');
      var packs = document.querySelector('.Vtl-NewVolumeDiscountRadio:not(.quick-add-modal *)');
      if (!slot || !packs || slot.contains(packs)) return;
      slot.appendChild(vitalsRoot(packs, slot));
      var skel = slot.querySelector('.wb-skel-packs');
      if (skel) skel.remove();
    }
    var queued = false;
    // Kept on: a size / colour change re-renders the section and Vitals adds its widget again
    new MutationObserver(function () {
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () {
        queued = false;
        place();
      });
    }).observe(document.body, { childList: true, subtree: true });
    place();
  })();

  // Product page: move the Vitals delivery date line into its fixed slot under the buy buttons, found by its text
  // since Vitals gives it generated class names (the Vitals trust badges were switched off in Oct 2026)
  (function () {
    if (!document.querySelector('[data-wb-trust-slot]')) return;
    var widgets = [/Order now to get it by/i];
    function find(re, slot) {
      var match = Array.prototype.find.call(
        document.querySelectorAll('main [class*="vtl" i], main [id*="vitals" i]'),
        function (el) { return !el.closest('.quick-add-modal') && re.test(el.textContent || ''); }
      );
      return match ? vitalsRoot(match, slot) : null;
    }
    function place() {
      var slot = document.querySelector('[data-wb-trust-slot]:not(.quick-add-modal *)');
      if (!slot) return;
      dropSkeletonLater(slot, '.wb-skel-trust');
      var placed = 0;
      widgets.forEach(function (re) {
        var root = find(re, slot);
        if (!root) return;
        placed += 1;
        if (!slot.contains(root)) slot.appendChild(root);
      });
      var skel = slot.querySelector('.wb-skel-trust');
      if (skel && placed === widgets.length) skel.remove();
    }
    var queued = false;
    // Kept on: a size / colour change re-renders the buy buttons and Vitals adds its widgets again
    new MutationObserver(function () {
      if (queued) return;
      queued = true;
      setTimeout(function () {
        queued = false;
        place();
      }, 100);
    }).observe(document.body, { childList: true, subtree: true });
    place();
  })();

  // Footer sign-up: only the form that was submitted (its id is in the URL hash) keeps its thank-you / error
  (function () {
    var id = location.hash.slice(1);
    document.querySelectorAll('[data-wb-form-state]').forEach(function (body) {
      var form = body.closest('form');
      if (body.dataset.wbFormState && (!form || form.id !== id)) body.dataset.wbFormState = '';
    });
  })();

  // #anchor on load: from 990px the page scrolls inside .page-wrapper, so scroll that, not the locked document
  window.addEventListener('load', function () {
    var target = location.hash && document.getElementById(decodeURIComponent(location.hash.slice(1)));
    var wrap = document.querySelector('.page-wrapper');
    if (!target || !wrap || !window.matchMedia('(min-width: 990px)').matches) return;
    var header = document.querySelector('#header-group');
    document.documentElement.scrollTop = 0;
    wrap.scrollTop += target.getBoundingClientRect().top - ((header && header.offsetHeight) || 100) - 24;
  });

  // Size / colour change: shimmer the parts that are about to update (photo, price, stock, SKU, pack prices)
  (function () {
    var pending = 0;
    var timer;
    var safety;
    var changedIndex = '';
    // which option row was clicked: the rows after it depend on it (availability, photos) and refresh too
    document.addEventListener('change', function (event) {
      var target = event.target;
      if (!(target instanceof Element) || !target.closest('variant-picker')) return;
      var row = target.closest('[data-fieldset-index]');
      changedIndex = row ? row.getAttribute('data-fieldset-index') : '';
    }, true);
    function finish() {
      pending = Math.max(0, pending - 1);
      if (pending) return;
      clearTimeout(safety);
      clearTimeout(timer);
      // hold a moment: Vitals redraws its pack prices just after the section re-render
      timer = setTimeout(function () {
        document.body.removeAttribute('data-wb-variant-loading');
        document.body.removeAttribute('data-wb-changed');
      }, 500);
    }
    document.addEventListener('shopify:product:select', function (event) {
      if (event.target instanceof Element && event.target.closest('product-card')) return;
      clearTimeout(timer);
      clearTimeout(safety);
      document.body.setAttribute('data-wb-variant-loading', '');
      document.body.setAttribute('data-wb-changed', changedIndex);
      safety = setTimeout(function () {
        pending = 0;
        document.body.removeAttribute('data-wb-variant-loading');
        document.body.removeAttribute('data-wb-changed');
      }, 8000);
      pending++;
      var promise = event.promise;
      if (promise && typeof promise.then === 'function') promise.then(finish, finish);
      else finish();
    });
  })();

  // Klaviyo pop-ups (back in stock): find the panel behind the form content and mark it, so CSS can give it the site look
  (function () {
    var queued = false;
    function isFilled(color) {
      return color && color !== 'transparent' && color !== 'rgba(0, 0, 0, 0)';
    }
    function mark(dialog) {
      var components = dialog.querySelectorAll('[data-testid="form-component"]');
      if (!components.length) return;
      var panels = [];
      var el = components[0].parentElement;
      while (el && el !== dialog) {
        if (isFilled(getComputedStyle(el).backgroundColor) || el.hasAttribute('data-wb-kl-panel')) panels.push(el);
        el = el.parentElement;
      }
      panels.forEach(function (panel, index) {
        panel.setAttribute('data-wb-kl-panel', index === panels.length - 1 ? 'outer' : 'inner');
      });
      // the headline is the first, short text block
      var first = components[0];
      if (first && (first.textContent || '').trim().length < 60) first.setAttribute('data-wb-kl-heading', '');
    }
    function run() {
      queued = false;
      document.querySelectorAll('[role="dialog"][class*="kl-private-reset-css"]').forEach(mark);
    }
    new MutationObserver(function () {
      if (queued) return;
      queued = true;
      requestAnimationFrame(run);
    }).observe(document.body, { childList: true, subtree: true });
  })();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor: re-run when sections or blocks are re-rendered
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:block:select', init);
})();
